/**
 * The figures the business records by hand on its weekly reconciliation sheet (TASKID-3629).
 *
 * `source` and `basis` are both carried because basis alone is ambiguous: "ytd 1,234,567"
 * cannot say whether it is Paid year-to-date or Submitted year-to-date, and the reconciliation
 * rollup keeps those as separate measures on the same line.
 */

/** Which measure the figure is for. */
export type ReconciliationManualFigureSource = 'paid' | 'submit';

/** Whether the figure covers the period alone or the year to its end. */
export type ReconciliationManualFigureBasis = 'week' | 'ytd';

export type ReconciliationManualFigure = {
  id: number;
  /** Period the figure belongs to, matching the ingestion report date. */
  reportDate: string;
  source: ReconciliationManualFigureSource;
  basis: ReconciliationManualFigureBasis;
  /** Recruiter code as entered; the line is identified by this, never by its label. */
  recruiter: string;
  /**
   * Label to display. Resolved from the Carriers table when the code matches, otherwise the
   * override stored on the row, otherwise null — which is how an unresolved code surfaces.
   */
  label: string | null;
  /** Carrier the line belongs to; the grid groups by it. Null when the code is unresolved. */
  carrierName: string | null;
  /** True when `recruiter` matches no line in the Carriers table. */
  unresolved: boolean;
  /**
   * What a person typed. Null on a line only the weekly carrier ingest has filled in, which is
   * most of them now (TASKID-3644): the ingest pre-fills what it can and a typed figure
   * replaces it, so the two are kept apart and a re-upload can never reach an edit.
   */
  amount: number | null;
  /**
   * What the modified file totals for this line. Null on a line the ingest has never pre-filled
   * -- a carrier with no pipeline contract, or a period that predates the pre-fill.
   */
  finalAmount: number | null;
  /** When the ingest last pre-filled this line. Null when it never has. */
  autoUpdatedAt: string | null;
  updatedAt: string;
  updatedByName: string | null;
  updatedByEmail: string | null;
};

export type ReconciliationManualFiguresGetListResponse = {
  reportDate: string;
  figures: ReconciliationManualFigure[];
};

export type ReconciliationManualFigureUpsertRequest = {
  reportDate: string;
  source: ReconciliationManualFigureSource;
  basis: ReconciliationManualFigureBasis;
  recruiter: string;
  label?: string | null;
  amount: number;
};

export type ReconciliationManualFigureDeleteRequest = {
  id: number;
};

/**
 * One line of the grid as it is offered for entry, before anything is recorded.
 *
 * The modal prefills from the lines visible for the chosen measure so that entry is typing
 * amounts rather than assembling rows, which is what the paper sheet it replaces looks like.
 */
export type ReconciliationManualFigureTemplateLine = {
  recruiter: string;
  label: string;
  /** Carrier the line belongs to, so the picker and the grid can group by it. */
  carrierName: string;
  sortOrder: number;
};

export type ReconciliationManualFigureTemplateResponse = {
  reportDate: string;
  source: ReconciliationManualFigureSource;
  lines: ReconciliationManualFigureTemplateLine[];
};
